import React from 'react';

import PropTypes from 'prop-types';

import {
  defaultValueTagRenderer,
  defaultFooterRenderer,
  defaultMenuItemRenderer,
} from './helpers/utils';
import { useAssigneePicker } from './hooks';
import MultiAssigneePicker from './layout/MultiAssigneePicker';
import MultiAutocompleteAssigneePicker from './layout/MultiAutocompleteAssigneePicker';
import SingleAssigneePicker from './layout/SingleAssigneePicker';
import SingleAutocompleteAssigneePicker from './layout/SingleAutocompleteAssigneePicker';
import { AssigneePickerProps } from './types';

const AssigneePicker = ({
  multiple = true,
  searchable = true,
  container = document.body,
  companyOptionValue,
  roleOptionValue,
  userOptionValue,
  companyOptionLabel,
  roleOptionLabel,
  userOptionLabel,
  optionsOrder,
  statusFilters,
  optionsFilter,
  additionalCompanyDataFields,
  additionalUserDataFields,
  additionalRoleDataFields,
  customUsers,
  customRoles,
  customCompanies,
  postFormatters,
  ...baseProps
}: AssigneePickerProps) => {
  const { groupedItems: items, isLoading } = useAssigneePicker({
    companyOptionValue,
    roleOptionValue,
    userOptionValue,
    companyOptionLabel,
    roleOptionLabel,
    userOptionLabel,
    optionsOrder,
    statusFilters,
    optionsFilter,
    additionalCompanyDataFields,
    additionalUserDataFields,
    additionalRoleDataFields,
    customUsers,
    customRoles,
    customCompanies,
    postFormatters,
  });
  const props = { ...baseProps, container, isLoading, items };

  if (multiple) {
    const key = `${items.length > 0 || process.env.NODE_ENV === 'test'}`;

    if (searchable) {
      return <MultiAutocompleteAssigneePicker key={key} {...props} />;
    } else {
      return <MultiAssigneePicker key={key} {...props} />;
    }
  } else {
    if (searchable) {
      return <SingleAutocompleteAssigneePicker {...props} />;
    } else {
      return <SingleAssigneePicker {...props} />;
    }
  }
};

AssigneePicker.defaultValueTagRenderer = defaultValueTagRenderer;
AssigneePicker.defaultMenuItemRenderer = defaultMenuItemRenderer;
AssigneePicker.defaultFooterRenderer = defaultFooterRenderer;

AssigneePicker.propTypes = {
  ...useAssigneePicker.propTypes,
  /**
   * whether or not to show an icon next to the selected option
   */
  showIcons: PropTypes.bool,
  /**
   * whether or not the filter will reset when an option is selected
   */
  clearFilterOnSelect: PropTypes.bool,
  /**
   * Whether the component is multi selectable
   */
  multiple: PropTypes.bool,
  /**
   * Whether the component is searchable
   */
  searchable: PropTypes.bool,
  /**
   * Dropdown hook props
   */
  dropdownProps: PropTypes.object,
  /**
   * Whether to render the Clear footer
   */
  clearable: PropTypes.bool,
  /**
   *  onChange callback
   */
  onChange: PropTypes.func,
  /**
   * renderer for the Dropdown Control
   */
  renderControl: PropTypes.func,
  /**
   *  renderer for the Dropdown Menu
   */
  renderMenu: PropTypes.func,
  /**
   * renderer for the Virtualized Menu Footer
   */
  renderVirtualizedMenuFooter: PropTypes.func,
  /**
   * renderer for the Menu Item
   */
  renderMenuItem: PropTypes.func,
};

export default AssigneePicker;
